import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  /** @type {number} */
  get currentCartCount() {
    return parseInt(this.refs.cartBubbleCount.textContent ?? '0', 10);
  }

  set currentCartCount(value) {
    this.refs.cartBubbleCount.textContent = value < 100 ? String(value) : '';
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    this.ensureCartBubbleIsCorrect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
  }

  /**
   * Handles the cart update event.
   * @param {CartUpdateEvent} event - The cart update event.
   */
  onCartUpdate = async (event) => {
    const itemCount = event.detail.data?.itemCount ?? 0;
    const comingFromProductForm = event.detail.data?.source === 'product-form-component';

    this.renderCartBubble(itemCount, comingFromProductForm);
  };

  /**
   * Renders the cart bubble.
   * @param {number} itemCount - The number of items in the cart.
   * @param {boolean} comingFromProductForm - Whether the cart update is coming from the product form.
   */
  renderCartBubble = async (itemCount, comingFromProductForm, animate = true) => {
    // Product-form events send the added quantity; cart-page events send the absolute total.
    const parsedCurrent = Number.parseInt(this.refs.cartBubbleCount.textContent ?? '', 10);
    const currentCount = Number.isFinite(parsedCurrent) ? parsedCurrent : 0;
    const nextCount = comingFromProductForm ? currentCount + itemCount : itemCount;

    this.refs.cartBubbleCount.classList.toggle('hidden', nextCount === 0);
    this.refs.cartBubble.classList.toggle('visually-hidden', nextCount === 0);
    this.refs.cartBubble.classList.toggle('cart-bubble--animating', nextCount > 0 && animate);

    this.currentCartCount = nextCount;
    this.classList.toggle('header-actions__cart-icon--has-cart', nextCount > 0);

    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(nextCount),
        timestamp: Date.now(),
      })
    );

    if (!animate) return;
    await onAnimationEnd(this.refs.cartBubbleText);

    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };

  /**
   * Applies a recent optimistic cart count from sessionStorage after navigation.
   * Never overrides a server-rendered empty cart (stale counts caused a ghost badge).
   */
  ensureCartBubbleIsCorrect = () => {
    const sessionStorageCount = sessionStorage.getItem('cart-count');
    if (sessionStorageCount === null) return;

    // Server rendered an empty cart — clear any stale optimistic count.
    if (this.refs.cartBubble.classList.contains('visually-hidden')) {
      sessionStorage.setItem(
        'cart-count',
        JSON.stringify({
          value: '0',
          timestamp: Date.now(),
        })
      );
      return;
    }

    try {
      const { value, timestamp } = JSON.parse(sessionStorageCount);
      const sessionCount = Number.parseInt(value, 10);
      const visibleCount = Number.parseInt(this.refs.cartBubbleCount.textContent ?? '', 10) || 0;

      if (!Number.isFinite(sessionCount) || sessionCount === visibleCount) return;

      if (Date.now() - timestamp < 10000 && sessionCount > 0) {
        this.renderCartBubble(sessionCount, false, false);
      }
    } catch (_) {
      // no-op
    }
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
